import {
  LoginTypeEnum,
  OTPEnum,
  ProfileRolesEnum,
  ProfileVerificationStatusEnum,
  Remark,
} from '@protos/user_management/ums.db_pb';
import {
  TeacherLoginResponseType,
  UserInfo,
} from '@protos/user_management/ums.login.apis_pb';
import { SchoolData } from '@protos/user_management/ums.self_registration.apis_pb';
import { createSlice } from '@reduxjs/toolkit';
// import {} from '@protos/common/'

interface IOtpInfo {
  verification_code: string;
  otp_type: OTPEnum;
}
interface InfoToast {
  label: string | React.ReactElement;
  variant: 'success' | 'error' | 'info';
  open: boolean;
}
export interface SelectedClassSectionSubject {
  class_id: number;
  class_name: string;
  section_id: bigint;
  section_name: string;
  subject_id: number;
  subject_name: string;
}
interface ISsoData {
  session_mode?: 'teach' | 'assess';
  is_sso_user?: boolean;
  sso_subject_ids?: number[];
}
export interface IAuthState {
  user_info?: TeacherLoginResponseType;
  contact_popup_open: boolean;
  skipped_feature_screens: boolean;
  login_Type?: LoginTypeEnum;
  login_uid?: string;
  login_phone?: string;
  login_email?: string;
  multiple_profiles?: TeacherLoginResponseType[];
  otp_info?: IOtpInfo;
  verify_path: '/verify-phone' | '/verify-email' | '';
  toast_info: InfoToast;
  password_verification?: string;
  user_validation_info?: UserInfo;
  is_mcb_user?: boolean;
  is_logged_in_offline?: boolean;
  is_mobile_number_changed?: boolean;
  is_email_id_changed?: boolean;
  self_registration_flow?: boolean;
  self_registration_data?: {
    user_profile_id?: string;
    first_name?: string;
    last_name?: string;
    school_code?: string;
    school_name?: string;
    role?: ProfileRolesEnum;
    phone_number?: string;
    detailed_class_section_subjects?: SelectedClassSectionSubject[];
    profile_verification_data?: TeacherLoginResponseType;
    last_reminder_sent?: Date;
    profile_verification_status?: ProfileVerificationStatusEnum;
    rejection_remarks?: Remark;
  };
  school_data?: SchoolData;
  ssoRedirectionUrl?: string;
  isLoggingOut?: boolean;
  sso_data: ISsoData;
}

const initialState: IAuthState = {
  user_info: undefined,
  contact_popup_open: false,
  skipped_feature_screens: true,
  verify_path: '',
  toast_info: { label: '', variant: 'success', open: false },
  is_mcb_user: false,
  is_logged_in_offline: false,
  self_registration_flow: false,
  is_mobile_number_changed: false,
  is_email_id_changed: false,
  isLoggingOut: false,
  sso_data: {},
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUserInfo: (state, action) => {
      // console.log('payload', action.payload);
      state.user_info = action.payload;
    },
    setNewFirstname: (state, action) => {
      if (state.user_info) {
        state.user_info.firstName = action.payload;
      }
    },
    setContactPopupOpen: (state, action) => {
      state.contact_popup_open = action.payload;
    },
    setSkippedFeatureScreens: (state, action) => {
      state.skipped_feature_screens = action.payload as boolean;
    },
    setLoginUID: (state, action) => {
      if (state.login_phone) {
        state.login_phone = undefined;
      }
      if (state.login_email) {
        state.login_email = undefined;
      }
      state.login_uid = action.payload;
      state.login_Type = LoginTypeEnum.LOGIN_TYPE_USERNAME;
    },
    setLoginPhone: (state, action) => {
      if (state.login_uid) {
        state.login_uid = undefined;
      }
      if (state.login_email) {
        state.login_email = undefined;
      }
      state.login_phone = action.payload;
      state.login_Type = LoginTypeEnum.LOGIN_TYPE_PHONE_NUMBER;
    },
    setLoginEmail: (state, action) => {
      if (state.login_phone) {
        state.login_phone = undefined;
      }
      if (state.login_uid) {
        state.login_uid = undefined;
      }
      state.login_email = action.payload;
      state.login_Type = LoginTypeEnum.LOGIN_TYPE_EMAIL;
    },
    setMultipleProfiles: (state, action) => {
      state.multiple_profiles = action.payload;
    },
    setOtpInfo: (state, action) => {
      state.otp_info = action.payload;
    },

    setVerifyPath: (state, action) => {
      state.verify_path = action.payload;
    },
    setToastInfo: (state, action) => {
      state.toast_info = action.payload;
    },
    setPasswordVerificationToken: (state, action) => {
      state.password_verification = action.payload;
    },
    SetUserValidationInfo: (state, action) => {
      state.user_validation_info = action.payload;
    },
    setIsMCBUser: (state, action) => {
      state.is_mcb_user = action.payload;
    },
    resetAuthState: (state) => {
      return initialState;
    },
    setIsLoggedInOffline: (state, action) => {
      state.is_logged_in_offline = action.payload;
    },
    setSelfRegistrationFlow: (state, action) => {
      state.self_registration_flow = action.payload;
    },
    setSelfRegistrationData: (state, action) => {
      state.self_registration_data = action.payload;
    },
    setSchoolData: (state, action) => {
      state.school_data = action.payload;
    },
    setSsoRedirectionUrl: (state, action) => {
      state.ssoRedirectionUrl = action.payload;
    },
    setMobilenumberChanged: (state, action) => {
      state.is_mobile_number_changed = action.payload;
    },
    setEmailIdChanged: (state, action) => {
      state.is_email_id_changed = action.payload;
    },
    setIsLoggingOut: (state, action: { payload: boolean }) => {
      state.isLoggingOut = action.payload;
    },
    setSsoData: (state, action: { payload: ISsoData }) => {
      state.sso_data = action.payload;
    },
  },
});

export const {
  setUserInfo,
  setNewFirstname,
  resetAuthState,
  setContactPopupOpen,
  setSkippedFeatureScreens,
  setToastInfo,
  setLoginUID,
  setLoginPhone,
  setLoginEmail,
  setMultipleProfiles,
  setOtpInfo,
  setPasswordVerificationToken,
  SetUserValidationInfo,
  setVerifyPath,
  setIsMCBUser,
  setIsLoggedInOffline,
  setSelfRegistrationFlow,
  setSelfRegistrationData,
  setSchoolData,
  setSsoRedirectionUrl,
  setMobilenumberChanged,
  setEmailIdChanged,
  setIsLoggingOut,
  setSsoData,
} = authSlice.actions;
export default authSlice.reducer;
